import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Tag } from 'antd';
import {
  modifyCancelStatusColors,
  modifyCancelStatusLabels,
  modifyCancelTypeColors,
  modifyCancelTypeLabels,
  orderSideColors,
  orderSideLabels,
  orderStatusColors,
  orderTypeColors,
} from 'apps/investment/constants/stockOrder';
import { DataTableColumnHeader } from 'common/data-table-column-header';
import { Button } from 'common/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'common/ui/tooltip';
import {
  currencyCell,
  dateTimeCell,
  linkCell,
  numberCell,
  userCell,
} from 'utils/cell-formatter';

export const columnsGenerator = () => [
  {
    accessorKey: 'id',
    header: '',
    cell: ({ getValue }) =>
      linkCell({ getValue, path: '/investment/stock-orders' }),
  },
  {
    accessorKey: 'order_date',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Order Date" />
    ),
    cell: dateTimeCell,
  },
  {
    accessorKey: 'order_side',
    header: 'Order Side',
    cell: ({ getValue }) => {
      const value = getValue();
      return (
        <Tag color={orderSideColors[value]}>
          {orderSideLabels[value] || value}
        </Tag>
      );
    },
  },
  {
    accessorKey: 'order_type',
    header: 'Order Type',
    cell: ({ getValue }) => {
      const value = getValue();
      return <Tag color={orderTypeColors[value]}>{value?.toUpperCase()}</Tag>;
    },
  },
  {
    accessorKey: 'order_source',
    header: 'Order Source',
    cell: ({ getValue }) => {
      const value = getValue();
      const color = value === 'stag' ? 'blue' : 'orange';
      return <Tag color={color}>{value?.toUpperCase()}</Tag>;
    },
  },
  {
    accessorKey: 'symbol',
    header: 'Symbol',
  },

  {
    header: 'Status',
    accessorKey: 'status',
    cell: ({ row, getValue }) => {
      const status = getValue();
      const label = row.original.status_label;
      const security_order_message = row.original.security_order_message;

      if (security_order_message) {
        return (
          <div>
            {security_order_message && (
              <TooltipProvider delayDuration={0}>
                <Tooltip>
                  <TooltipTrigger asChild className="p-0">
                    <Button variant="none">
                      <Tag
                        color={orderStatusColors[status]}
                        icon={<ExclamationCircleOutlined />}
                      >
                        {label}
                      </Tag>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{security_order_message}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        );
      }
      return <Tag color={orderStatusColors[status]}>{label}</Tag>;
    },
  },
  {
    accessorKey: 'price',
    header: 'Price',
    cell: currencyCell,
  },
  {
    accessorKey: 'volume',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Volume" />
    ),
    cell: numberCell,
  },
  {
    accessorKey: 'value',
    header: 'Value',
    cell: currencyCell,
  },
  {
    accessorKey: 'matched_price',
    header: 'Matched Price',
    cell: currencyCell,
  },

  {
    accessorKey: 'matched_volume',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Matched Volume" />
    ),
    cell: numberCell,
  },

  {
    accessorKey: 'matched_value',
    header: 'Matched Value',
    cell: currencyCell,
  },
  {
    header: 'User',
    accessorKey: 'user',
    cell: userCell,
  },
  {
    accessorKey: 'created_at',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Created" />
    ),
    cell: dateTimeCell,
  },
  {
    accessorKey: 'updated_at',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Updated" />
    ),
    cell: dateTimeCell,
  },
];

export const modifyCancelColumnsGenerator = () => [
  {
    accessorKey: 'type',
    header: 'Loại',
    cell: ({ getValue }) => {
      const value = getValue();
      return (
        <Tag color={modifyCancelTypeColors[value]}>
          {modifyCancelTypeLabels[value] || value}
        </Tag>
      );
    },
  },
  {
    accessorKey: 'status',
    header: 'Trạng thái',
    cell: ({ row, getValue }) => {
      const status = getValue();
      const label = row.original.status_label || modifyCancelStatusLabels[status];
      return <Tag color={modifyCancelStatusColors[status]}>{label}</Tag>;
    },
  },
  {
    accessorKey: 'original_volume',
    header: 'Original Volume',
    cell: numberCell,
  },
  {
    accessorKey: 'original_price',
    header: 'Original Price',
    cell: currencyCell,
  },
  {
    accessorKey: 'volume',
    header: 'New Volume',
    cell: numberCell,
  },
  {
    accessorKey: 'price',
    header: 'New Price',
    cell: currencyCell,
  },
  {
    accessorKey: 'security_order_id',
    header: 'Security Order ID',
  },
  {
    accessorKey: 'security_order_time',
    header: 'Security Order Time',
    cell: dateTimeCell,
  },
  {
    accessorKey: 'security_order_message',
    header: 'Security Order Message',
  },
  {
    accessorKey: 'created_at',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Created" />
    ),
    cell: dateTimeCell,
  },
];

import { stockOrderStatusLabels } from 'apps/investment/constants/stockOrder';
import { FilterFieldConfig } from 'common/dynamic-filter-form';

// Generate status options from constants (excluding 0 values)
const statusOptions = Object.entries(stockOrderStatusLabels)
  .filter(([value]) => Number(value) > 0)
  .map(([value, label]) => ({
    value,
    label,
  }));

export const filterConfig: FilterFieldConfig[] = [
  {
    name: 'status',
    type: 'select',
    label: 'Status',
    minWidth: '100px',
    defaultValue: 'all',
    options: statusOptions,
  },
  {
    name: 'order_side',
    type: 'select',
    label: 'Order Side',
    minWidth: '100px',
    defaultValue: 'all',
    options: [
      { value: 'buy', label: 'Buy' },
      { value: 'sell', label: 'Sell' },
    ],
  },
  {
    name: 'order_type',
    type: 'select',
    label: 'Order Type',
    minWidth: '100px',
    defaultValue: 'all',
    options: [
      { value: 'lo', label: 'LO' },
      { value: 'mp', label: 'MP' },
      { value: 'ato', label: 'ATO' },
      { value: 'atc', label: 'ATC' },
    ],
  },
  {
    name: 'order_source',
    type: 'select',
    label: 'Order Source',
    minWidth: '100px',
    defaultValue: 'all',
    options: [
      { value: 'stag', label: 'Stag' },
      { value: 'nhsv', label: 'NHSV' },
    ],
  },
];
